import { generatePath } from 'react-router-dom'

import { TERMINATED_SUBSCRIPTION_STATUSES } from '@snootsvet/shared/portal'

import { usePetWithSubscription } from '@/customer/hooks/use-pet-with-subscription'
import type { PetIssue } from '@/customer/lib/membership'
import { routes } from '@/customer/routes'

export function usePetIssues(petId: string): { issue: PetIssue | null; isPending: boolean } {
  const { pet, membership, isPending } = usePetWithSubscription(petId)

  if (isPending || !pet || !membership) return { issue: null, isPending: true }

  const status = membership.subscription?.status ?? null

  if (status === 'incomplete') {
    return {
      issue: {
        type: 'incomplete_subscription',
        title: `We couldn't take payment for ${pet.name}'s membership`,
        action: {
          href: generatePath(routes.account.pets.paymentFailed, { patientId: pet.id }),
          label: 'Complete payment',
        },
      },
      isPending: false,
    }
  }

  if (status && TERMINATED_SUBSCRIPTION_STATUSES.has(status)) {
    return {
      issue: {
        type: 'lapsed_subscription',
        title: `${pet.name}'s membership has lapsed`,
        action: {
          href: generatePath(routes.account.pets.plan, { patientId: pet.id }),
          label: 'Activate membership',
        },
      },
      isPending: false,
    }
  }

  if (!status && !pet.hasSubscription) {
    return {
      issue: {
        type: 'no_subscription',
        severity: 'info',
        title: `${pet.name} does not have an active Snoots membership`,
        action: {
          href: generatePath(routes.account.pets.plan, { patientId: pet.id }),
          label: 'Activate membership',
        },
      },
      isPending: false,
    }
  }

  return { issue: null, isPending: false }
}
